import React, { useState } from 'react';
import api from '../services/api';
import './PatientRecords.css';

function PatientRecords() {
  const [view, setView] = useState('search'); // search, details, edit
  const [searchCriteria, setSearchCriteria] = useState({
    phoneNumber: '',
    dateOfBirth: '',
    email: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientAppointments, setPatientAppointments] = useState([]);
  const [editedPatient, setEditedPatient] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSearchChange = (field, value) => {
    setSearchCriteria(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setSearchResults([]);

    // Validate at least one search criterion
    if (!searchCriteria.phoneNumber && !searchCriteria.dateOfBirth && !searchCriteria.email) {
      setError('Please enter at least one search criterion');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/patients/verify', searchCriteria);

      if (response.data.success) {
        // If exact match found - backend returns patient in response.data.data
        const patient = response.data.data;
        setSearchResults([patient]);

        // Automatically select if only one result
        await handleSelectPatient(patient);
      } else {
        setError('No patients found matching the criteria');
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err.response?.data?.error || 'Failed to search for patients');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPatient = async (patient) => {
    setSelectedPatient(patient);
    setEditedPatient({ ...patient });
    setView('details');
    setError('');
    setSuccess('');

    // Load patient's appointments
    try {
      const patientId = patient.id || patient.pid || patient.patient_id;
      const response = await api.get(`/patients/${patientId}/appointments`);
      setPatientAppointments(response.data.data || response.data || []);
    } catch (err) {
      console.error('Error loading appointments:', err);
      // Don't show error, just set empty appointments
      setPatientAppointments([]);
    }
  };

  const handleEditChange = (field, value) => {
    setEditedPatient(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveChanges = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const patientId = selectedPatient.id || selectedPatient.pid || selectedPatient.patient_id;
      const response = await api.put(`/patients/${patientId}`, editedPatient);

      if (response.data.success) {
        setSuccess('Patient information updated successfully');
        // Merge updated patient data with edited data to preserve all fields
        const updatedPatient = {
          ...editedPatient,
          ...(response.data.data || {})
        };
        setSelectedPatient(updatedPatient);
        setEditedPatient(updatedPatient);
        setView('details');
      }
    } catch (err) {
      console.error('Error updating patient:', err);
      setError(err.response?.data?.error || 'Failed to update patient information');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSearch = () => {
    setView('search');
    setSelectedPatient(null);
    setEditedPatient(null);
    setPatientAppointments([]);
    setSearchResults([]);
    setError('');
    setSuccess('');
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getAppointmentStatus = (status) => {
    switch (status) {
      case '@': return 'Arrived';
      case '~': return 'Cancelled';
      case '-':
      default: return 'Pending';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case '@': return '#28a745';
      case '~': return '#dc3545';
      case '-':
      default: return '#ffc107';
    }
  };

  return (
    <div className="patient-records">
      <div className="records-header">
        <h2>Patient Records</h2>
        {view !== 'search' && (
          <button className="back-btn" onClick={handleBackToSearch}>
            ← Back to Search
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>
          {success}
        </div>
      )}

      {/* Search View */}
      {view === 'search' && (
        <div className="search-section">
          <div className="search-card">
            <h3>Search for Patient</h3>
            <p className="search-description">
              Enter at least one search criterion to find a patient record
            </p>

            <form onSubmit={handleSearch} className="search-form">
              <div className="form-group">
                <label>Phone Number:</label>
                <input
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={searchCriteria.phoneNumber}
                  onChange={(e) => handleSearchChange('phoneNumber', e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Date of Birth:</label>
                <input
                  type="date"
                  value={searchCriteria.dateOfBirth}
                  onChange={(e) => handleSearchChange('dateOfBirth', e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Email Address:</label>
                <input
                  type="email"
                  placeholder="patient@example.com"
                  value={searchCriteria.email}
                  onChange={(e) => handleSearchChange('email', e.target.value)}
                  className="form-input"
                />
              </div>

              <button
                type="submit"
                className="btn-primary btn-large"
                disabled={loading}
              >
                {loading ? 'Searching...' : '🔍 Search Patients'}
              </button>
            </form>
          </div>

          {searchResults.length > 0 && (
            <div className="search-results">
              <h3>Search Results ({searchResults.length})</h3>
              <div className="results-grid">
                {searchResults.map((patient, index) => (
                  <div
                    key={patient.id || patient.pid || index}
                    className="patient-result-card"
                    onClick={() => handleSelectPatient(patient)}
                  >
                    <div className="patient-name">
                      {patient.firstName || patient.fname} {patient.lastName || patient.lname}
                    </div>
                    <div className="patient-info-row">
                      <span>📞 {patient.phoneNumber || patient.phone_home || 'N/A'}</span>
                    </div>
                    <div className="patient-info-row">
                      <span>🎂 {formatDate(patient.dateOfBirth || patient.DOB)}</span>
                    </div>
                    <div className="view-details-btn">View Details →</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Details View */}
      {view === 'details' && selectedPatient && (
        <div className="details-section">
          <div className="details-header">
            <h3>
              {selectedPatient.firstName || selectedPatient.fname} {selectedPatient.lastName || selectedPatient.lname}
            </h3>
            <button
              className="btn-secondary"
              onClick={() => setView('edit')}
            >
              ✏️ Edit Information
            </button>
          </div>

          <div className="details-grid">
            {/* Personal Information */}
            <div className="info-card">
              <h4>Personal Information</h4>
              <div className="info-row">
                <span className="info-label">Full Name:</span>
                <span className="info-value">
                  {selectedPatient.firstName || selectedPatient.fname} {selectedPatient.lastName || selectedPatient.lname}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Date of Birth:</span>
                <span className="info-value">
                  {formatDate(selectedPatient.dateOfBirth || selectedPatient.DOB)}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Gender:</span>
                <span className="info-value">
                  {selectedPatient.sex || 'Not specified'}
                </span>
              </div>
            </div>

            {/* Contact Information */}
            <div className="info-card">
              <h4>Contact Information</h4>
              <div className="info-row">
                <span className="info-label">Phone:</span>
                <span className="info-value">
                  {selectedPatient.phoneNumber || selectedPatient.phone_home || 'N/A'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Email:</span>
                <span className="info-value">
                  {selectedPatient.email || 'N/A'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Address:</span>
                <span className="info-value">
                  {[
                    selectedPatient.address || selectedPatient.street,
                    selectedPatient.city,
                    selectedPatient.state,
                    selectedPatient.zipCode || selectedPatient.postal_code
                  ].filter(Boolean).join(', ') || 'N/A'}
                </span>
              </div>
            </div>

            {/* Insurance Information */}
            <div className="info-card">
              <h4>Insurance Information</h4>
              <div className="info-row">
                <span className="info-label">Provider:</span>
                <span className="info-value">
                  {selectedPatient.insuranceProvider || selectedPatient.primary_insurance_company || 'N/A'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Policy Number:</span>
                <span className="info-value">
                  {selectedPatient.insurancePolicyNumber || selectedPatient.policy_number || 'N/A'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Group Number:</span>
                <span className="info-value">
                  {selectedPatient.insuranceGroupNumber || selectedPatient.group_number || 'N/A'}
                </span>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="info-card">
              <h4>Emergency Contact</h4>
              <div className="info-row">
                <span className="info-label">Name:</span>
                <span className="info-value">
                  {selectedPatient.emergencyContact || selectedPatient.contact_name || 'N/A'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Phone:</span>
                <span className="info-value">
                  {selectedPatient.emergencyPhone || selectedPatient.contact_phone || 'N/A'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Relationship:</span>
                <span className="info-value">
                  {selectedPatient.contact_relationship || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Appointment History */}
          <div className="appointments-section">
            <h4>Appointment History</h4>
            {patientAppointments.length === 0 ? (
              <p className="no-data">No appointments found</p>
            ) : (
              <div className="appointments-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Reason</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {patientAppointments.map((apt, index) => (
                      <tr key={apt.id || apt.pc_eid || index}>
                        <td>{formatDate(apt.date || apt.pc_eventDate)}</td>
                        <td>{formatTime(apt.time || apt.pc_startTime)}</td>
                        <td>{apt.reason || apt.pc_title || 'Office Visit'}</td>
                        <td>
                          <span
                            className="status-dot"
                            style={{ backgroundColor: getStatusColor(apt.status || apt.pc_apptstatus) }}
                          >
                            {getAppointmentStatus(apt.status || apt.pc_apptstatus)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit View */}
      {view === 'edit' && editedPatient && (
        <div className="edit-section">
          <div className="edit-header">
            <h3>Edit Patient Information</h3>
          </div>

          <div className="edit-form">
            {/* Personal Information */}
            <div className="form-section">
              <h4>Personal Information</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name:</label>
                  <input
                    type="text"
                    value={editedPatient.firstName || editedPatient.fname || ''}
                    onChange={(e) => handleEditChange('firstName', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Last Name:</label>
                  <input
                    type="text"
                    value={editedPatient.lastName || editedPatient.lname || ''}
                    onChange={(e) => handleEditChange('lastName', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date of Birth:</label>
                  <input
                    type="date"
                    value={(editedPatient.dateOfBirth || editedPatient.DOB || '').split('T')[0]}
                    onChange={(e) => handleEditChange('dateOfBirth', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Gender:</label>
                  <select
                    value={editedPatient.sex || ''}
                    onChange={(e) => handleEditChange('sex', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="form-section">
              <h4>Contact Information</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Phone Number:</label>
                  <input
                    type="tel"
                    value={editedPatient.phoneNumber || editedPatient.phone_home || ''}
                    onChange={(e) => handleEditChange('phoneNumber', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Email:</label>
                  <input
                    type="email"
                    value={editedPatient.email || ''}
                    onChange={(e) => handleEditChange('email', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Street Address:</label>
                <input
                  type="text"
                  value={editedPatient.address || editedPatient.street || ''}
                  onChange={(e) => handleEditChange('address', e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City:</label>
                  <input
                    type="text"
                    value={editedPatient.city || ''}
                    onChange={(e) => handleEditChange('city', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>State:</label>
                  <input
                    type="text"
                    value={editedPatient.state || ''}
                    onChange={(e) => handleEditChange('state', e.target.value)}
                    className="form-input"
                    maxLength="2"
                  />
                </div>
                <div className="form-group">
                  <label>ZIP Code:</label>
                  <input
                    type="text"
                    value={editedPatient.zipCode || editedPatient.postal_code || ''}
                    onChange={(e) => handleEditChange('zipCode', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            {/* Insurance Information */}
            <div className="form-section">
              <h4>Insurance Information</h4>
              <div className="form-group">
                <label>Insurance Provider:</label>
                <input
                  type="text"
                  value={editedPatient.insuranceProvider || editedPatient.primary_insurance_company || ''}
                  onChange={(e) => handleEditChange('insuranceProvider', e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Policy Number:</label>
                  <input
                    type="text"
                    value={editedPatient.insurancePolicyNumber || editedPatient.policy_number || ''}
                    onChange={(e) => handleEditChange('insurancePolicyNumber', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Group Number:</label>
                  <input
                    type="text"
                    value={editedPatient.insuranceGroupNumber || editedPatient.group_number || ''}
                    onChange={(e) => handleEditChange('insuranceGroupNumber', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="form-section">
              <h4>Emergency Contact</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Contact Name:</label>
                  <input
                    type="text"
                    value={editedPatient.emergencyContact || editedPatient.contact_name || ''}
                    onChange={(e) => handleEditChange('emergencyContact', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Contact Phone:</label>
                  <input
                    type="tel"
                    value={editedPatient.emergencyPhone || editedPatient.contact_phone || ''}
                    onChange={(e) => handleEditChange('emergencyPhone', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setEditedPatient({ ...selectedPatient });
                  setView('details');
                }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveChanges}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PatientRecords;
